import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { toast } from 'sonner';
import { 
  Mail, 
  Phone, 
  User,
  MessageSquare,
  CheckCircle,
  Trash2,
  ExternalLink,
  Filter
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ContactRequestsManagement = () => {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all'); // all, unread, read

  useEffect(() => {
    fetchRequests();
  }, []);

  const fetchRequests = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/contact-requests`);
      setRequests(response.data);
    } catch (error) {
      console.error('Error fetching contact requests:', error);
      toast.error('Erreur lors du chargement des demandes');
    } finally {
      setLoading(false);
    }
  };

  const markAsRead = async (requestId) => {
    try {
      await axios.put(`${API}/contact-requests/${requestId}/read`);
      toast.success('Demande marquée comme lue');
      fetchRequests();
    } catch (error) {
      console.error('Error marking as read:', error);
      toast.error('Erreur lors de la mise à jour');
    }
  };

  const getRequestTypeBadge = (type) => {
    const typeMap = {
      devis: { label: 'Devis', className: 'bg-blue-100 text-blue-800' },
      info: { label: 'Information', className: 'bg-purple-100 text-purple-800' },
      reclamation: { label: 'Réclamation', className: 'bg-red-100 text-red-800' },
      autre: { label: 'Autre', className: 'bg-gray-100 text-gray-800' }
    };
    
    const info = typeMap[type] || typeMap.autre;
    return <Badge className={info.className}>{info.label}</Badge>;
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('fr-FR', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const filteredRequests = requests.filter(req => {
    if (filter === 'unread') return !req.is_read;
    if (filter === 'read') return req.is_read;
    return true;
  });

  const unreadCount = requests.filter(r => !r.is_read).length;

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="container mx-auto p-6">
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-gray-800 flex items-center gap-2">
          <MessageSquare className="w-8 h-8" />
          Demandes de Contact
        </h1>
        <p className="text-gray-600 mt-2">
          {requests.length} demande{requests.length > 1 ? 's' : ''} au total
          {unreadCount > 0 && ` • ${unreadCount} non lue${unreadCount > 1 ? 's' : ''}`}
        </p>
      </div>

      {/* Filters */}
      <Card className="mb-6">
        <CardContent className="p-4">
          <div className="flex items-center gap-2">
            <Filter className="w-4 h-4 text-gray-400" />
            <Button
              size="sm"
              variant={filter === 'all' ? 'default' : 'outline'}
              onClick={() => setFilter('all')}
            >
              Toutes ({requests.length})
            </Button>
            <Button
              size="sm"
              variant={filter === 'unread' ? 'default' : 'outline'}
              onClick={() => setFilter('unread')}
            >
              Non lues ({unreadCount})
            </Button>
            <Button
              size="sm"
              variant={filter === 'read' ? 'default' : 'outline'}
              onClick={() => setFilter('read')}
            >
              Lues ({requests.length - unreadCount})
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Requests List */}
      {filteredRequests.length === 0 ? (
        <Card className="text-center py-12">
          <CardContent>
            <MessageSquare className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-gray-900 mb-2">
              Aucune demande
            </h3>
            <p className="text-gray-600">
              Les demandes de contact apparaîtront ici
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {filteredRequests.map((request) => (
            <Card 
              key={request.id} 
              className={`${!request.is_read ? 'border-l-4 border-l-blue-500 bg-blue-50' : 'bg-white'}`}
            >
              <CardContent className="p-6">
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div className={`w-12 h-12 rounded-full ${!request.is_read ? 'bg-blue-200' : 'bg-gray-200'} flex items-center justify-center`}>
                      <User className={`w-6 h-6 ${!request.is_read ? 'text-blue-600' : 'text-gray-600'}`} />
                    </div>
                    <div>
                      <h3 className="text-lg font-bold text-gray-900">
                        {request.name}
                      </h3>
                      <p className="text-sm text-gray-500">
                        {formatDate(request.created_at)}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {!request.is_read && (
                      <Badge className="bg-blue-600 text-white">Nouveau</Badge>
                    )}
                    {getRequestTypeBadge(request.request_type)}
                  </div>
                </div>

                <div className="mb-4">
                  <h4 className="font-semibold text-gray-900 mb-2">{request.subject}</h4>
                  <p className="text-gray-700 whitespace-pre-wrap">{request.message}</p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-4 p-3 bg-gray-50 rounded-lg">
                  <div className="flex items-center gap-2">
                    <Phone className="w-4 h-4 text-gray-400" />
                    <a href={`tel:${request.phone}`} className="text-sm text-blue-600 hover:underline">
                      {request.phone}
                    </a>
                  </div>
                  <div className="flex items-center gap-2">
                    <Mail className="w-4 h-4 text-gray-400" />
                    <a href={`mailto:${request.email}`} className="text-sm text-blue-600 hover:underline truncate">
                      {request.email}
                    </a>
                  </div>
                </div>

                <div className="flex flex-wrap gap-2">
                  {!request.is_read && (
                    <Button 
                      size="sm" 
                      onClick={() => markAsRead(request.id)}
                      className="bg-blue-600 hover:bg-blue-700"
                    >
                      <CheckCircle className="w-4 h-4 mr-1" />
                      Marquer comme lu
                    </Button>
                  )}
                  
                  <Button 
                    size="sm" 
                    variant="outline"
                    onClick={() => window.location.href = `mailto:${request.email}?subject=Re: ${request.subject}`}
                  >
                    <Mail className="w-4 h-4 mr-1" />
                    Répondre par email
                  </Button>

                  <Button 
                    size="sm" 
                    variant="outline"
                    className="text-green-600 hover:bg-green-50"
                    onClick={() => window.open(`https://wa.me/${request.phone.replace(/\s/g, '')}`, '_blank')}
                  >
                    <ExternalLink className="w-4 h-4 mr-1" />
                    WhatsApp
                  </Button>
                </div>

                {request.replied_at && (
                  <div className="mt-3 pt-3 border-t border-gray-200">
                    <p className="text-xs text-gray-500">
                      <CheckCircle className="w-3 h-3 inline mr-1" />
                      Lu le {formatDate(request.replied_at)}
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

export default ContactRequestsManagement;
